import React from 'react';
import { flushSync } from 'react-dom';
import TransactionalSheet from '../../components/chat/sidebar/TransactionalSheet';
import {
  CarrierMark,
  LegRow,
  carriersLabel,
  ctaHelp,
  ctaLabel,
  duration,
  modeOf,
  money,
  plusDays,
  Rating,
  SkeletonCard,
  timeOf,
  ukDate,
} from './cards';
import {
  outboundFares,
  returnFares,
  type LegFare,
  type ReturnFlight,
  type Segment,
  type StayOption,
  type StayRate,
  type StayRoom,
  type TransportOption,
  type TravelOption,
} from './data';

const minutesBetween = (a: string, b: string) =>
  Math.round(
    (new Date(`${b}Z`).getTime() - new Date(`${a}Z`).getTime()) / 60000,
  );

function Choice({
  on,
  onPick,
  title,
  titleDetail,
  sub,
  price,
  note,
}: {
  on: boolean;
  onPick: () => void;
  title: string;
  titleDetail?: string;
  sub: React.ReactNode;
  price: string;
  note?: string;
}) {
  return (
    <button
      type='button'
      className={`to-choice${on ? ' to-choice--on' : ''}`}
      onClick={onPick}
      aria-pressed={on}
    >
      <span className='to-choice__text'>
        <strong>
          {title}
          {titleDetail && (
            <>
              {' '}
              <span className='to-choice__detail'>{titleDetail}</span>
            </>
          )}
        </strong>
        <span>{sub}</span>
      </span>
      <span className='to-choice__price'>
        {price}
        {note && <span className='to-choice__breakdown'>{note}</span>}
      </span>
    </button>
  );
}

function Place({
  code,
  name,
  terminal,
}: {
  code: string;
  name?: string;
  terminal?: string;
}) {
  return (
    <span className='to-tl__place'>
      {name ?? <b>{code}</b>}
      {terminal && <span className='to-tl__term'>Terminal {terminal}</span>}
    </span>
  );
}

// The assistant's reading of a return against the trip: bold line, one line of detail.
function ReturnNote({
  note,
}: {
  note?: { headline: string; detail?: string };
}) {
  if (!note) return null;
  return (
    <p className='to-choice__note'>
      <strong>{note.headline}</strong>
      {note.detail && <span>{note.detail}</span>}
    </p>
  );
}

function SegmentBlock({ s, train }: { s: Segment; train: boolean }) {
  const days = plusDays(s.dep, s.arr);
  const meta = [s.number, s.aircraft].filter(Boolean).join(', ');
  return (
    <li className='to-tl__seg'>
      <div className='to-tl__carrier'>
        <CarrierMark code={s.carrierCode} name={s.carrierName} train={train} />
        <span>{s.carrierName}</span>
        <span className='to-tl__meta'>{meta}</span>
      </div>
      {s.operatedBy && s.operatedBy !== s.carrierName && (
        <p className='to-tl__op'>Operated by {s.operatedBy}</p>
      )}
      <div className='to-tl__grid'>
        <span className='to-tl__time'>{timeOf(s.dep)}</span>
        <i className='to-tl__dot' />
        <Place code={s.from} name={s.fromName} terminal={s.fromTerminal} />
        <span />
        <i className='to-tl__line' />
        <span className='to-tl__dur'>{duration(s.durationMin)}</span>
        <span className='to-tl__time'>
          {timeOf(s.arr)}
          {days > 0 && <sup>+{days}</sup>}
        </span>
        <i className='to-tl__dot' />
        <Place code={s.to} name={s.toName} terminal={s.toTerminal} />
      </div>
    </li>
  );
}

function ChangeRow({
  prev,
  next,
  train,
}: {
  prev: Segment;
  next: Segment;
  train: boolean;
}) {
  const wait = duration(minutesBetween(prev.arr, next.dep));
  const moveStation =
    train && prev.toName && next.fromName && prev.toName !== next.fromName;
  return (
    <li className='to-tl__change'>
      <ClockGlyph />
      <span>
        <b>{wait} change</b>
        {moveStation
          ? `, ${prev.toName} to ${next.fromName}`
          : ` in ${prev.toName ?? prev.to}`}
      </span>
    </li>
  );
}

function ClockGlyph() {
  return (
    <svg viewBox='0 0 24 24' aria-hidden='true'>
      <circle
        cx='12'
        cy='12'
        r='8.25'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.5'
      />
      <path
        d='M12 7.5V12l3 2'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
      />
    </svg>
  );
}

function TransportDetail({
  o,
  choice,
  setChoice,
}: {
  o: TransportOption;
  choice: number;
  setChoice: (i: number) => void;
}) {
  const train = o.kind === 'train';
  return (
    <>
      {o.legs.map((leg) => (
        <section key={leg.label + leg.dep} className='to-section'>
          <h4 className='to-tl__title'>
            <span>
              {leg.label}, {ukDate(leg.dep)}
            </span>
            <span className='to-tl__total'>
              {duration(leg.durationMin)} total
            </span>
          </h4>
          <ol className='to-tl'>
            {leg.segments.map((s, i) => (
              <React.Fragment key={s.number + s.dep}>
                {i > 0 && (
                  <ChangeRow
                    prev={leg.segments[i - 1]}
                    next={s}
                    train={train}
                  />
                )}
                <SegmentBlock s={s} train={train} />
              </React.Fragment>
            ))}
          </ol>
        </section>
      ))}
      {o.mode === 'select' ? (
        <p className='to-muted'>
          This is your outbound flight. Next you choose the return, and the
          price covers both flights.
        </p>
      ) : o.mode === 'agent' ? (
        <p className='to-muted'>
          Dream books this on the airline’s own website.
        </p>
      ) : (
        <section className='to-section'>
          <h4>{train ? 'Ticket' : 'Fare'}</h4>
          {o.fares.map((f, i) => (
            <Choice
              key={f.id}
              on={i === choice}
              onPick={() => setChoice(i)}
              title={f.name}
              titleDetail={f.nameDetail}
              sub={[f.bags, f.conditions].filter(Boolean).join('. ')}
              price={money(f.total, f.currency, true)}
            />
          ))}
        </section>
      )}
    </>
  );
}

function About({ text }: { text: string }) {
  const [open, setOpen] = React.useState(false);
  return (
    <section className='to-section'>
      <h4>About</h4>
      <p className={`to-muted to-about${open ? '' : ' to-about--clamp'}`}>
        {text}
      </p>
      {!open && text.length > 160 && (
        <button type='button' className='to-link' onClick={() => setOpen(true)}>
          More
        </button>
      )}
    </section>
  );
}

const roomFacts = (room: StayRoom) =>
  [
    room.beds,
    room.sizeSqm ? `${room.sizeSqm} m²` : null,
    room.sleeps ? `sleeps ${room.sleeps}` : null,
  ]
    .filter(Boolean)
    .join(', ');

const rateTerms = (r: StayRate) =>
  r.refundable
    ? r.freeUntil
      ? `Free cancellation until ${ukDate(r.freeUntil)}`
      : 'Free cancellation'
    : 'Non-refundable';

function RoomBlock({
  room,
  o,
  choice,
  setChoice,
}: {
  room: StayRoom;
  o: StayOption;
  choice: number;
  setChoice: (i: number) => void;
}) {
  const facts = roomFacts(room);
  return (
    <div className='to-room'>
      <div className='to-room__head'>
        {room.photo && <img src={room.photo} alt='' loading='lazy' />}
        <div>
          <strong>{room.name}</strong>
          {facts && <span>{facts}</span>}
          {room.amenities.length > 0 && (
            <span>{room.amenities.slice(0, 4).join(', ')}</span>
          )}
        </div>
      </div>
      {room.rates.map((i) => {
        const r = o.rates[i];
        return (
          <Choice
            key={r.id}
            on={i === choice}
            onPick={() => setChoice(i)}
            title={r.board}
            sub={rateTerms(r)}
            price={money(r.total, r.currency, true)}
            note={
              r.perNight != null && o.nights > 1
                ? `${money(r.perNight, r.currency, true)}/night`
                : undefined
            }
          />
        );
      })}
    </div>
  );
}

function StayDetail({
  o,
  choice,
  setChoice,
}: {
  o: StayOption;
  choice: number;
  setChoice: (i: number) => void;
}) {
  return (
    <>
      {o.photos.length > 0 && (
        <div
          className={`to-gallery${o.photos.length === 1 ? ' to-gallery--single' : ''}`}
        >
          {o.photos.slice(0, 6).map((p) => (
            <img key={p} src={p} alt='' loading='lazy' />
          ))}
        </div>
      )}
      {o.address && <p className='to-muted'>{o.address}</p>}
      {o.description && <About text={o.description} />}
      {o.rooms.length > 0 ? (
        <section className='to-section'>
          <h4>Rooms</h4>
          {o.rooms.map((room) => (
            <RoomBlock
              key={room.name + room.rates.join()}
              room={room}
              o={o}
              choice={choice}
              setChoice={setChoice}
            />
          ))}
        </section>
      ) : (
        <p className='to-muted'>
          Dream books this directly with the property and confirms the room
          before you pay.
        </p>
      )}
      {o.facilities.length > 0 && (
        <section className='to-section'>
          <h4>Facilities</h4>
          <p className='to-muted'>{o.facilities.slice(0, 6).join(', ')}</p>
        </section>
      )}
    </>
  );
}

export function totalFor(o: TravelOption, choice: number) {
  if (o.kind === 'hotel')
    return o.rates[choice] ?? { total: o.price, currency: o.currency };
  return o.fares[choice] ?? { total: o.price, currency: o.currency };
}

export function headline(o: TravelOption) {
  if (o.kind !== 'flight' && o.kind !== 'train') return o.title;
  const l = o.legs[0];
  const last = l.segments[l.segments.length - 1];
  return `${l.segments[0].fromName ?? l.from} to ${last.toName ?? l.to}`;
}

function subline(o: TravelOption) {
  if (o.kind === 'hotel')
    return `${ukDate(o.checkIn)} – ${ukDate(o.checkOut)}, ${o.guests} ${o.guests === 1 ? 'guest' : 'guests'}`;
  return carriersLabel(o);
}

export function DetailsSheet({
  option,
  onClose,
  onBook,
  bookingDisabled = false,
}: {
  option: TravelOption | null;
  onClose: () => void;
  onBook: (o: TravelOption, choice: number) => void;
  bookingDisabled?: boolean;
}) {
  const [choice, setChoice] = React.useState(0);
  React.useEffect(() => setChoice(0), [option?.id]);
  if (!option) return null;
  const total = totalFor(option, choice);
  return (
    <TransactionalSheet
      show
      onClose={onClose}
      className='to-sheet'
      ariaLabelledBy='to-sheet-title'
      footer={
        <div className='to-sheet__foot'>
          <div className='to-price'>
            <strong>
              {money(total.total, total.currency, modeOf(option) === 'instant')}
            </strong>
            <span>{modeOf(option) === 'instant' ? 'total price' : 'from'}</span>
          </div>
          <button
            type='button'
            className='tx-btn-primary to-cta'
            disabled={bookingDisabled}
            onClick={() => onBook(option, choice)}
          >
            {ctaLabel(option)}
          </button>
          {ctaHelp(option) && <p className='to-cta-help'>{ctaHelp(option)}</p>}
        </div>
      }
    >
      <div className='tx-head'>
        <h1 className='tx-title' id='to-sheet-title'>
          {headline(option)}
        </h1>
        <p className='tx-sub'>{subline(option)}</p>
        {option.kind === 'hotel' && option.rating && (
          <p className='tx-sub to-sheet-rating'>
            <Rating o={option} />
          </p>
        )}
      </div>
      {option.kind === 'hotel' ? (
        <StayDetail o={option} choice={choice} setChoice={setChoice} />
      ) : (
        <TransportDetail o={option} choice={choice} setChoice={setChoice} />
      )}
    </TransactionalSheet>
  );
}

// Untitled UI "plane" icon.
const PlaneIcon = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M17.745 2.813a2.483 2.483 0 0 1 3.58 3.442l-2.78 2.91c-.217.228-.326.342-.393.475a.983.983 0 0 0-.104.38c-.01.148.025.302.096.61l1.728 7.487c.072.315.109.473.097.626a.999.999 0 0 1-.11.386c-.072.136-.186.25-.415.479l-.37.37c-.607.607-.91.91-1.22.965a1 1 0 0 1-.77-.182c-.253-.188-.389-.594-.66-1.408l-2.01-6.029-3.345 3.345c-.2.2-.3.3-.366.418a.997.997 0 0 0-.118.336c-.022.134-.006.274.025.555l.184 1.653c.03.28.046.42.025.554a.999.999 0 0 1-.118.337c-.067.117-.166.217-.366.417l-.198.198c-.473.473-.71.71-.972.778a1 1 0 0 1-.69-.069c-.244-.118-.43-.397-.8-.953L6.106 18.54a1.845 1.845 0 0 0-.138-.194.998.998 0 0 0-.111-.112c-.045-.038-.095-.071-.194-.138L3.31 16.528c-.556-.371-.835-.557-.954-.8a1 1 0 0 1-.068-.69c.069-.263.305-.5.778-.973l.198-.197c.2-.2.3-.3.417-.366.104-.06.218-.1.336-.118.134-.022.274-.006.555.025l1.653.184c.281.03.421.046.555.025a.998.998 0 0 0 .336-.118c.118-.067.218-.166.417-.366l3.346-3.345-6.03-2.01c-.813-.271-1.22-.407-1.407-.66a1 1 0 0 1-.182-.77c.055-.31.358-.613.964-1.22l.37-.37c.23-.23.344-.344.48-.414a1 1 0 0 1 .386-.111c.153-.012.31.024.626.097l7.459 1.721c.31.072.465.108.615.097a1 1 0 0 0 .411-.122c.132-.072.242-.186.464-.415l2.71-2.799Z' />
  </svg>
);

const flightNumbers = (leg: TransportOption['legs'][number]) =>
  leg.segments.map((s) => s.number).join(' + ');

function LegFares({
  fares,
  on,
  setOn,
  wholeTrip = false,
}: {
  fares: LegFare[];
  on: number;
  setOn: (i: number) => void;
  wholeTrip?: boolean;
}) {
  return (
    <section className='to-section'>
      <h4>Cabin and bags</h4>
      {fares.map((f, i) => (
        <Choice
          key={f.id}
          on={i === on}
          onPick={() => setOn(i)}
          title={f.name}
          titleDetail={f.nameDetail}
          sub={[f.bags, f.conditions].filter(Boolean).join('. ')}
          price={
            wholeTrip
              ? money(f.price, f.currency, true)
              : i === 0
                ? 'Included'
                : `+${money(f.price - fares[0].price, f.currency, true)}`
          }
        />
      ))}
    </section>
  );
}

type Page = 'out' | 'returns' | 'back';
type Dir = 'fwd' | 'back';
type VTDocument = Document & {
  startViewTransition?: (update: () => void) => { finished: Promise<void> };
};

const canMorph = () =>
  'startViewTransition' in document &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const tagMorph = (key: string, name: string | null) =>
  document
    .querySelectorAll<HTMLElement>(`[data-morph="${key}"]`)
    .forEach((el) =>
      name
        ? el.style.setProperty('view-transition-name', name)
        : el.style.removeProperty('view-transition-name'),
    );

const asLegFares = (o: TransportOption): LegFare[] =>
  o.fares.map((f) => ({
    id: f.id,
    name: f.name,
    price: f.total,
    currency: f.currency,
    bags: f.bags,
    conditions: f.conditions,
  }));

export function FlightSheet({
  flight,
  sameTicket = [],
  separateTickets = [],
  onClose,
  onBookOne,
  onBookPair,
  onOtherReturns,
  bookingDisabled = false,
  returnsLoading = false,
  returnsNotice,
  returnsFailure,
  returnRefreshNotice,
  onRetryReturns,
  tryAgainLabel = 'Try again',
  backLabel = 'Back',
  bookingRefreshing = false,
  bookingNotice,
  gettingLivePricesLabel = 'Getting live prices…',
}: {
  flight: TransportOption | null;
  sameTicket?: ReturnFlight[];
  separateTickets?: ReturnFlight[];
  onClose: () => void;
  onBookOne: (o: TransportOption, choice: number) => void;
  onBookPair: (
    o: TransportOption,
    r: ReturnFlight,
    fares: [LegFare, LegFare],
    farePositions: [number, number],
  ) => void;
  onOtherReturns?: () => void;
  bookingDisabled?: boolean;
  returnsLoading?: boolean;
  returnsNotice?: string | null;
  returnsFailure?: { title: string; body: string } | null;
  returnRefreshNotice?: string | null;
  onRetryReturns?: () => void;
  tryAgainLabel?: string;
  backLabel?: string;
  bookingRefreshing?: boolean;
  bookingNotice?: string | null;
  gettingLivePricesLabel?: string;
}) {
  const returns = React.useMemo(
    () =>
      [...sameTicket, ...separateTickets].sort((a, b) =>
        a.dep.localeCompare(b.dep),
      ),
    [sameTicket, separateTickets],
  );
  const pairedRoundTrip = Boolean(
    flight?.mode === 'instant' &&
      flight.pairedSearch &&
      flight.legs.length === 2,
  );
  const pair = flight?.mode === 'select' || returns.length > 0;
  const outFares = React.useMemo(
    () => (!flight ? [] : pair ? outboundFares(flight) : asLegFares(flight)),
    [flight, pair],
  );
  const [page, setPage] = React.useState<Page>('out');
  const [dir, setDir] = React.useState<Dir | null>(null);
  const [outFare, setOutFare] = React.useState(0);
  const [picked, setPicked] = React.useState<string | null>(null);
  const [backFare, setBackFare] = React.useState(0);
  const top = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    setPage('out');
    setDir(null);
    setPicked(null);
    setOutFare(0);
    setBackFare(0);
  }, [flight?.id]);
  if (!flight) return null;

  const out = flight.legs[0];
  const ret = returns.find((r) => r.id === picked) ?? null;
  const backFares = ret ? returnFares(ret) : [];
  const o = outFares[outFare] ?? outFares[0];
  const b = backFares[backFare] ?? backFares[0];
  const selectedReturnFare =
    ret &&
    (b ?? {
      id: ret.choiceId ?? ret.id,
      name: 'Standard',
      price: ret.price,
      currency: ret.currency,
      bags: null,
      conditions: '',
    });
  const from = returns.length
    ? Math.min(...returns.map((returned) => returned.price))
    : o.price;
  const morph = canMorph();

  const go = (next: Page, d: Dir, update?: () => void, key?: string) => {
    const apply = () => {
      flushSync(() => {
        update?.();
        setDir(d);
        setPage(next);
      });
      const body = top.current?.closest('.tx-body');
      if (body) body.scrollTop = 0;
      if (key) tagMorph(key, 'to-vt-ret');
    };
    if (!morph) return apply();
    const root = document.documentElement;
    root.dataset.toVt = d;
    if (key) tagMorph(key, 'to-vt-ret');
    (document as VTDocument).startViewTransition!(apply).finished.finally(
      () => {
        delete root.dataset.toVt;
        if (key) tagMorph(key, null);
      },
    );
  };
  const toReturns = () => go('returns', 'fwd');
  const backToOut = () => go('out', 'back');
  const backToReturns = () =>
    go('returns', 'back', undefined, ret ? `ret-${ret.id}` : undefined);
  const pickReturn = (r: ReturnFlight) =>
    go(
      'back',
      'fwd',
      () => {
        setPicked(r.id);
        setBackFare(0);
      },
      `ret-${r.id}`,
    );

  const outCard = (change: boolean) => (
    <div className='to-pinned to-pinned--out'>
      <LegRow leg={out} train={false} />
      <p className='to-pinned__info'>
        {[flightNumbers(out), o.name, o.bags].filter(Boolean).join(', ')}
      </p>
      {change && (
        <button type='button' className='to-link' onClick={backToOut}>
          Change
        </button>
      )}
    </div>
  );

  const returnDate = flight.returnDate || returns[0]?.dep;
  const head = {
    out: {
      title: pairedRoundTrip
        ? 'Your trip'
        : pair
          ? 'Your outbound'
          : 'Your flight',
      sub: `${out.from} to ${out.to}, ${ukDate(out.dep)}`,
    },
    returns: {
      title: 'Choose your return',
      sub: `${out.to} to ${out.from}${returnDate ? `, ${ukDate(returnDate)}` : ''}`,
    },
    back: {
      title: 'Your return',
      sub: ret ? `${ret.from} to ${ret.to}, ${ukDate(ret.dep)}` : '',
    },
  }[page];

  const price = (text: string, label: string) => (
    <div className='to-price'>
      <strong key={text} className='to-tick'>
        {text}
      </strong>
      <span>{label}</span>
    </div>
  );
  const footer =
    page === 'back' && ret && selectedReturnFare ? (
      <div className='to-sheet__foot'>
        {price(
          `${money(selectedReturnFare.price, selectedReturnFare.currency, true)}${returnRefreshNotice ? '*' : ''}`,
          'total for both flights',
        )}
        <button
          type='button'
          className='tx-btn-primary to-cta'
          disabled={bookingDisabled || bookingRefreshing}
          onClick={() =>
            onBookPair(
              flight,
              ret,
              [o, selectedReturnFare],
              [outFare, backFare],
            )
          }
        >
          {bookingRefreshing ? gettingLivePricesLabel : 'Book both flights'}
        </button>
      </div>
    ) : page === 'returns' ? (
      <div className='to-sheet__foot'>
        {price(
          `from ${money(from, flight.currency)}${returnRefreshNotice ? '*' : ''}`,
          'choose a return flight',
        )}
      </div>
    ) : pair ? (
      <div className='to-sheet__foot'>
        {price(`from ${money(from, flight.currency)}`, 'round trip')}
        <button
          type='button'
          className='tx-btn-primary to-cta'
          disabled={bookingDisabled}
          onClick={toReturns}
        >
          Select return flight
        </button>
      </div>
    ) : (
      <div className='to-sheet__foot'>
        {price(money(o.price, o.currency, true), 'total price')}
        <button
          type='button'
          className='tx-btn-primary to-cta'
          disabled={bookingDisabled || bookingRefreshing}
          onClick={() => onBookOne(flight, outFare)}
        >
          {bookingRefreshing ? gettingLivePricesLabel : 'Book now'}
        </button>
      </div>
    );

  return (
    <TransactionalSheet
      show
      onClose={onClose}
      onBack={
        page === 'returns'
          ? backToOut
          : page === 'back'
            ? backToReturns
            : undefined
      }
      className='to-sheet to-sheet--flight'
      ariaLabelledBy='to-flight-title'
      footer={footer}
    >
      <div
        key={page}
        className={`to-flow${!morph && dir ? ` to-flow--${dir}` : ''}`}
      >
        <div className='tx-head' ref={top}>
          {pair && (
            <ol className='to-steps' aria-label='Booking steps'>
              <li
                className={page === 'out' ? 'is-on' : 'is-done'}
                aria-current={page === 'out' ? 'step' : undefined}
              >
                {page === 'out' ? (
                  <>
                    <span className='to-steps__dot'>1</span>
                    <span className='to-steps__label'>Outbound</span>
                  </>
                ) : (
                  <button
                    type='button'
                    className='to-steps__back'
                    aria-label='Outbound, done. Change outbound'
                    onClick={backToOut}
                  >
                    <span className='to-steps__dot' aria-hidden='true'>
                      ✓
                    </span>
                    <span className='to-steps__label'>Outbound</span>
                  </button>
                )}
              </li>
              <li
                className={page === 'out' ? undefined : 'is-on'}
                aria-current={page === 'out' ? undefined : 'step'}
              >
                <span className='to-steps__dot'>2</span>
                <span className='to-steps__label'>Return</span>
              </li>
            </ol>
          )}
          <h1 className='tx-title' id='to-flight-title'>
            {head.title}
          </h1>
          <p className='tx-sub'>{head.sub}</p>
        </div>
        {bookingNotice && (
          <p role='alert' className='to-choice__warning'>
            {bookingNotice}
          </p>
        )}
        {page === 'out' && (
          <>
            {pairedRoundTrip ? (
              flight.legs.map((leg) => (
                <section key={`${leg.label}-${leg.dep}`} className='to-section'>
                  <h4>
                    {leg.label}, {ukDate(leg.dep)}
                  </h4>
                  <div className='to-pinned to-pinned--out'>
                    <LegRow leg={leg} train={false} />
                    <p className='to-pinned__info'>{flightNumbers(leg)}</p>
                  </div>
                </section>
              ))
            ) : (
              <section className='to-section'>{outCard(false)}</section>
            )}
            <LegFares
              fares={outFares}
              on={outFare}
              setOn={setOutFare}
              wholeTrip={pairedRoundTrip}
            />
            {pairedRoundTrip &&
              onOtherReturns &&
              (flight.returnAlternatives ?? 0) > 0 && (
                <button
                  type='button'
                  className='to-link'
                  disabled={bookingDisabled}
                  onClick={onOtherReturns}
                >
                  Other return times ({flight.returnAlternatives})
                </button>
              )}
          </>
        )}
        {page === 'returns' && (
          <>
            <section className='to-section'>
              <h4>Your outbound, {ukDate(out.dep)}</h4>
              {outCard(true)}
            </section>
            {returnRefreshNotice && (
              <p
                className='to-muted'
                style={{ marginTop: 8 }}
                data-testid='return-countdown-sheet'
              >
                <span aria-hidden='true'>*</span>
                {returnRefreshNotice}
              </p>
            )}
            {returnsLoading ? (
              <div className='to-list' aria-label='Loading return flights'>
                <SkeletonCard kind='flight' />
              </div>
            ) : returnsFailure ? (
              <div className='to-return-failure' role='alert'>
                <span className='to-return-failure__icon'>
                  <PlaneIcon />
                </span>
                <h3 className='to-return-failure__title'>
                  {returnsFailure.title}
                </h3>
                <p className='to-return-failure__body'>{returnsFailure.body}</p>
                <div className='to-return-failure__actions'>
                  <button
                    type='button'
                    className='to-btn to-btn--ghost'
                    onClick={onRetryReturns}
                  >
                    {tryAgainLabel}
                  </button>
                  <button
                    type='button'
                    className='to-btn to-btn--ghost'
                    onClick={backToOut}
                  >
                    {backLabel}
                  </button>
                </div>
              </div>
            ) : returnsNotice ? (
              <>
                <p className='to-choice__warning' role='alert'>
                  {returnsNotice}
                </p>
                <div>
                  <button
                    type='button'
                    className='to-link'
                    onClick={onRetryReturns}
                  >
                    {tryAgainLabel}
                  </button>{' '}
                  <button type='button' className='to-link' onClick={backToOut}>
                    {backLabel}
                  </button>
                </div>
              </>
            ) : (
              <>
                <section className='to-section'>
                  <h4>Same-ticket returns</h4>
                  {sameTicket.map((r) => (
                    <button
                      key={r.id}
                      type='button'
                      data-morph={`ret-${r.id}`}
                      className='to-choice to-choice--leg'
                      onClick={() => pickReturn(r)}
                    >
                      <LegRow leg={r.leg} train={false} />
                      <ReturnNote note={r.note} />
                      <span className='to-choice__price'>
                        {r.fares && r.fares.length > 1 ? 'from ' : ''}
                        {money(r.price, r.currency)}
                        {returnRefreshNotice && '*'}
                        <span>round trip</span>
                      </span>
                    </button>
                  ))}
                </section>
                {separateTickets.length > 0 && (
                  <section className='to-section'>
                    <h4>Separate tickets</h4>
                    {separateTickets.map((r) => (
                      <button
                        key={r.id}
                        type='button'
                        data-morph={`ret-${r.id}`}
                        className='to-choice to-choice--leg'
                        onClick={() => pickReturn(r)}
                      >
                        <p className='to-muted'>
                          Booked as two separate tickets
                        </p>
                        <LegRow leg={r.leg} train={false} />
                        <ReturnNote note={r.note} />
                        <span className='to-choice__price'>
                          {money(r.price, r.currency)}
                          {returnRefreshNotice && '*'}
                          <span>two tickets</span>
                          {r.sourcePrices && r.sourcePrices.length > 1 && (
                            <span className='to-choice__breakdown'>
                              {r.sourcePrices
                                .map((source) =>
                                  money(source.total, source.currency),
                                )
                                .join(' + ')}
                            </span>
                          )}
                        </span>
                      </button>
                    ))}
                  </section>
                )}
                <p className='to-muted'>
                  Prices are for the outbound and return together.
                </p>
              </>
            )}
          </>
        )}
        {page === 'back' && ret && (
          <>
            <section className='to-section'>
              <h4>Your outbound, {ukDate(out.dep)}</h4>
              {outCard(true)}
            </section>
            {returnRefreshNotice && (
              <p
                className='to-muted'
                style={{ marginTop: 8 }}
                data-testid='return-countdown-sheet'
              >
                <span aria-hidden='true'>*</span>
                {returnRefreshNotice}
              </p>
            )}
            <section className='to-section'>
              <h4>Your return, {ukDate(ret.dep)}</h4>
              <div
                data-morph={`ret-${ret.id}`}
                className='to-choice to-choice--leg to-choice--on'
              >
                {ret.separateTickets && (
                  <p className='to-muted'>Booked as two separate tickets</p>
                )}
                <LegRow leg={ret.leg} train={false} />
                <ReturnNote note={ret.note} />
                <p className='to-pinned__info'>{flightNumbers(ret.leg)}</p>
                <button
                  type='button'
                  className='to-link'
                  onClick={backToReturns}
                >
                  Change
                </button>
              </div>
            </section>
            {backFares.length > 0 && (
              <LegFares
                fares={backFares}
                on={backFare}
                setOn={setBackFare}
                wholeTrip
              />
            )}
          </>
        )}
      </div>
    </TransactionalSheet>
  );
}
